import React, { useState } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import './forms.css';

const SuperAdminLoginForm = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [fieldTouched, setFieldTouched] = useState({
    username: false,
    password: false
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleInputBlur = (e) => {
    const { name } = e.target;
    setFieldTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    validateField(name, formData[name]);
  };

  const validateField = (fieldName, value) => {
    const errors = { ...validationErrors };
    
    switch (fieldName) {
      case 'username':
        if (!value.trim()) {
          errors.username = 'Username is required';
        } else if (value.length < 3) {
          errors.username = 'Username must be at least 3 characters';
        } else {
          delete errors.username;
        }
        break;
      case 'password':
        if (!value) {
          errors.password = 'Password is required';
        } else if (value.length < 6) {
          errors.password = 'Password must be at least 6 characters';
        } else {
          delete errors.password;
        }
        break;
      default:
        break;
    }
    
    setValidationErrors(errors);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form 
      className={`login-form ${isLoading ? 'form-loading' : ''}`} 
      onSubmit={handleSubmit}
      aria-label="Super admin login form"
      noValidate
    >
      <div className={`form-group ${validationErrors.username && fieldTouched.username ? 'error' : ''}`}>
        <label htmlFor="super-admin-username" className="form-label">
          Username
        </label>
        <div className="input-wrapper">
          <Input
            type="text"
            name="username"
            id="super-admin-username"
            placeholder="Enter your username"
            value={formData.username}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            error={fieldTouched.username ? validationErrors.username : ''}
            disabled={isLoading}
            required
            className="input-with-icon"
            aria-invalid={validationErrors.username ? "true" : "false"}
            autoComplete="username"
          />
          <div className="input-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
        </div>
      </div>
      
      <div className={`form-group ${validationErrors.password && fieldTouched.password ? 'error' : ''}`}>
        <label htmlFor="super-admin-password" className="form-label">
          Password
        </label>
        <div className="input-wrapper password-wrapper">
          <Input
            type={showPassword ? "text" : "password"}
            name="password"
            id="super-admin-password"
            placeholder="Enter your password"
            value={formData.password}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            error={fieldTouched.password ? validationErrors.password : ''}
            disabled={isLoading}
            required
            className="input-with-icon password-input"
            aria-invalid={validationErrors.password ? "true" : "false"}
            autoComplete="current-password"
          />
          <div className="input-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <button
            type="button"
            className="password-toggle"
            onClick={togglePasswordVisibility}
            disabled={isLoading}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            tabIndex="0"
            style={{ zIndex: 100 }}
          >
            {showPassword ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
          </button>
        </div>
      </div>
      
      <Button
        type="submit"
        variant="primary"
        size="large"
        disabled={isLoading || Object.keys(validationErrors).length > 0}
        className={`login-button ${isLoading ? 'btn-loading' : ''}`}
        aria-describedby="super-admin-login-help"
      >
        {isLoading ? (
          <>
            <div className="btn-spinner" aria-hidden="true"></div>
            <span aria-live="polite">Signing in...</span>
          </>
        ) : (
          'Sign In'
        )}
      </Button>
      
      <div id="super-admin-login-help" className="sr-only">
        Enter your username and password to sign in as platform administrator
      </div>
    </form>
  );
};

export default SuperAdminLoginForm;

